import { createContext, useState, useCallback, useRef } from "react";
import api from "../api/client";
import {
  hydrateDriveTripsFromServer,
  resetDriveTripSyncState,
} from "../utils/driveTripSync";

export const AuthContext = createContext({
  user: null,
  loading: true,
  userSessionChecked: false,
  places: [],
  defaultPlaceId: null,
  setUser: async () => {},
  refreshAuth: async () => {},
  refreshPlaces: async () => {},
  logout: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [places, setPlaces] = useState([]);
  const [defaultPlaceId, setDefaultPlaceId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userSessionChecked, setUserSessionChecked] = useState(false);
  const seq = useRef(0);

  const refreshPlaces = useCallback(async () => {
    try {
      const res = await api.get("/api/users/places", { skipSessionExpired: true });
      if (!res.data?.ok) throw new Error(res.data?.error?.message || "Failed");
      setPlaces(res.data.data?.places || []);
      setDefaultPlaceId(res.data.data?.defaultPlaceId || null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setPlaces([]);
        setDefaultPlaceId(null);
      } else {

      }
    }
  }, []);

  const setUser = useCallback((me, options = {}) => {
    const placesAlreadyLoaded = options?.placesAlreadyLoaded === true;
    if (!options.fromRefresh) {
      seq.current += 1;
      setUserSessionChecked(true);
      setLoading(false);
    }
    setUserState(me);
    if (me) {
      if (!placesAlreadyLoaded) refreshPlaces().catch(() => {});
      const uid = me.id || me._id;
      if (uid) {
        const run = () => hydrateDriveTripsFromServer(uid).catch(() => {});
        if (typeof window.requestIdleCallback === "function") {
          window.requestIdleCallback(run, { timeout: 2500 });
        } else {
          setTimeout(run, 600);
        }
      }
    } else {
      setPlaces([]);
      setDefaultPlaceId(null);
      resetDriveTripSyncState();
    }
  }, [refreshPlaces]);

  const refreshAuth = useCallback(() => {
    const id = ++seq.current;
    return (async () => {
      try {
        const boot = { timeout: 20_000, skipSessionExpired: true };
        const [meRes, placesRes] = await Promise.allSettled([
          api.get("/api/auth/user/me", boot),
          api.get("/api/users/places", boot),
        ]);
        if (id !== seq.current) return;

        const res = meRes.status === "fulfilled" ? meRes.value : null;
        const placesOk = placesRes.status === "fulfilled" && placesRes.value?.data?.ok;
        const applyPlaces = () => {
          if (!placesOk) return;
          setPlaces(placesRes.value.data.data?.places || []);
          setDefaultPlaceId(placesRes.value.data.data?.defaultPlaceId || null);
        };

        if (res?.data?.data?.user) {
          setUser(res.data.data.user, { fromRefresh: true, placesAlreadyLoaded: placesOk });
          applyPlaces();
          return;
        }

        if (res?.data?.type === "user" && res?.data?.userId) {
          setUser(
            {
              id: res.data.userId,
              email: res.data.email,
              role: res.data.role,
              accountType: "normal",
              studentStatus: "none",
              studentRefCode: "",
              studentValidUntil: null,
              needsStudentProfilePrompt: false,
              needsPhonePrompt: true,
              phoneNumber: "",
              studentShowIdAtRestaurantConsent: false,
              studentUnverifiedOfferUsed: false,
              dateOfBirth: null,
              studentDeclinedOwnerIds: [],
            },
            { fromRefresh: true, placesAlreadyLoaded: placesOk }
          );
          applyPlaces();
          return;
        }

        setUser(null, { fromRefresh: true });
      } catch {
        if (id !== seq.current) return;
        setUser(null, { fromRefresh: true });
      } finally {
        if (id === seq.current) {
          setUserSessionChecked(true);
          setLoading(false);
        }
      }
    })();
  }, [setUser]);

  const logout = async () => {
    try {
      const { removeDeviceTokenOnLogout } = await import("../app/FCMTokenHandler");
      await removeDeviceTokenOnLogout("user");
      await api.post("/api/google/logout");
    } catch {}
    resetDriveTripSyncState();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, userSessionChecked, places, defaultPlaceId, setUser, refreshAuth, refreshPlaces, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};
